import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { orpc, rpcClient } from '@/api/rpc/client';
import type {
  DeactivateAgreementRequest,
  DeactivateAgreementResponse,
  ReactivateAgreementResponse,
} from '@erp/rpc';
import type { RebateRule } from '@erp/shared-types';

/**
 * Hook to get agreements for a specific supplier
 */
export function useAgreementsBySupplier(supplierId: string | null) {
  return useQuery({
    ...orpc.rebates.agreements.getBySupplier.queryOptions({
      input: { supplierId: supplierId || '' },
    }),
    enabled: Boolean(supplierId),
  });
}

/**
 * Hook to get a single rebate agreement with full detail structure.
 * Migrated from useGetApi-based useRebateAgreement.
 */
export function useRebateAgreement(
  rebateAgreementId: string,
  enabled?: boolean,
) {
  return useQuery({
    ...orpc.rebates.agreements.getById.queryOptions({
      input: { rebateAgreementId },
    }),
    enabled: (enabled ?? true) && !!rebateAgreementId,
    select: (response) => response.data,
  });
}

/**
 * Hook to deactivate a finalized rebate agreement.
 * Wipes all non-reported breakdowns and excludes from forecasting.
 */
export function useDeactivateAgreement() {
  const queryClient = useQueryClient();

  return useMutation<
    DeactivateAgreementResponse,
    Error,
    DeactivateAgreementRequest
  >({
    mutationFn: ({ rebateAgreementId, reason, notes }) =>
      rpcClient.rebates.agreements.deactivate({
        rebateAgreementId,
        reason,
        notes,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['rebates/agreements'],
      });
      queryClient.invalidateQueries({
        queryKey: orpc.rebates.agreements.getById.key(),
      });
    },
  });
}

/**
 * Hook to reactivate a deactivated rebate agreement.
 * Breakdowns are not restored — user must regenerate forecasts.
 */
export function useReactivateAgreement() {
  const queryClient = useQueryClient();

  return useMutation<
    ReactivateAgreementResponse,
    Error,
    { rebateAgreementId: string }
  >({
    mutationFn: ({ rebateAgreementId }) =>
      rpcClient.rebates.agreements.reactivate({ rebateAgreementId }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['rebates/agreements'],
      });
      queryClient.invalidateQueries({
        queryKey: orpc.rebates.agreements.getById.key(),
      });
    },
  });
}

/**
 * Hook to analyze the impact of version changes before creating a new version.
 * Uses oRPC query (POST with body) — React Query handles dedup and caching.
 */
export function useAnalyzeVersionPreview(
  previousVersionId: string,
  extractedRules: RebateRule[],
  restoredRuleIds: string[],
  enabled: boolean,
) {
  return useQuery({
    ...orpc.rebates.agreements.analyzeVersionPreview.queryOptions({
      input: {
        previousVersionId,
        extractedRules: extractedRules as unknown as Record<string, unknown>[],
        restoredRuleIds,
      },
    }),
    enabled: enabled && !!previousVersionId,
  });
}
